/**
 * @file Filter descriptor to compare an entity property with a value.
 * @author Tom Verschoore de la Houssaije
 */
import { FilterOperators } from './filter-operators';
import { GroupFilter } from './group-filter';
import { Predicate, FilterQueryOp, DataType } from 'breeze-client';
import { HttpUrlEncodingCodec } from '@angular/common/http';
import * as moment from 'moment';
/**
 * Compares a entity property value with another value with a specific boolean operator.
 */
var PropertyFilter = /** @class */ (function () {
    /**
     * Constructor PropertyFilter.
     * The created PropertyFilter auto concatenate with the existing filters of the associated DataController
     *
     * @param component The associated DataController to add this filter descriptor.
     * @param concatenate The operator to use in the auto concatenation.
     * Only FilterOperators.And and FilterOperators.Or are valid.
     * The default value is FilterOperators.And.
     */
    function PropertyFilter(component, 
    /** Name of the entity property to evaluate in the comparison. */
    property, 
    /**
     * The compare operator. All FilterOperators are valid as operator, except the following:
     * FilterOperators.And
     * FilterOperators.Or
     * FilterOperators.Not
     */
    operator, 
    /** The value to compare with. */
    value, concatenate) {
        if (concatenate === void 0) { concatenate = FilterOperators.And; }
        this.property = property;
        this.operator = operator;
        this.value = value;
        // TODO: Do we need this?
        /** Gets sets the id for storing. */
        this.storeId = undefined;
        if (typeof component === 'undefined') {
            return;
        }
        if (concatenate !== FilterOperators.And && concatenate !== FilterOperators.Or) {
            throw new Error('Invalid value for concatenate parameter.');
        }
        if (component.filter) {
            component.filter = PropertyFilter.autoConcatenate(this, component.filter, concatenate);
            return;
        }
        component.filter = this;
    }
    /**
     * Method to support auto concatenate behavior.
     *
     * @param self The calling filter descriptor to be added.
     * @param filter The current filter to be incremented.
     * @param concatenate The operator to use in the auto concatenation. Only FilterOperators.And and FilterOperators.Or are valid.
     *
     * @returns Current filter after auto concatenation.
     */
    PropertyFilter.autoConcatenate = function (self, filter, concatenate) {
        if (concatenate === void 0) { concatenate = FilterOperators.And; }
        if (filter.operator === concatenate) {
            var group = filter;
            group.value.push(self);
            return filter;
        }
        return GroupFilter.create(filter, self, concatenate);
    };
    /**
     * Create method without auto concatenate behavior.
     *
     * @param property Name of the entity property to evaluate in the comparison.
     * @param operator The compare operator. All FilterOperators are valid as operator, except the following:
     * FilterOperators.And
     * FilterOperators.Or
     * FilterOperators.Not
     * @param value The value to compare with.
     *
     * @returns new PropertyFilter
     */
    PropertyFilter.create = function (property, operator, value) {
        return new PropertyFilter(undefined, property, operator, value);
    };
    /**
     * Gives a textual representation of the descriptor.
     *
     * @returns The description in pseudocode.
     */
    PropertyFilter.prototype.toText = function () {
        var result = this.property;
        switch (this.operator) {
            case FilterOperators.Equals:
                result += ' === ';
                break;
            case FilterOperators.GreaterThan:
                result += ' > ';
                break;
            case FilterOperators.GreaterThanOrEqual:
                result += ' >= ';
                break;
            case FilterOperators.LessThan:
                result += ' < ';
                break;
            case FilterOperators.LessThanOrEqual:
                result += ' <= ';
                break;
            case FilterOperators.NotEquals:
                result += ' !== ';
                break;
            case FilterOperators.Contains:
                result += ' contains ';
                break;
            default:
                throw new Error('Invalid operator!');
        }
        return result + this.value;
    };
    /**
     * Called internally to build the described query.
     * @param entityType The entity type getting filtered.
     * @returns The Predicate of this description.
     */
    PropertyFilter.prototype.createPredicate = function (entityType) {
        var encoder = new HttpUrlEncodingCodec();
        var breezeOperator = null;
        switch (this.operator) {
            case FilterOperators.Equals:
                breezeOperator = FilterQueryOp.Equals;
                break;
            case FilterOperators.GreaterThan:
                breezeOperator = FilterQueryOp.GreaterThan;
                break;
            case FilterOperators.GreaterThanOrEqual:
                breezeOperator = FilterQueryOp.GreaterThanOrEqual;
                break;
            case FilterOperators.LessThan:
                breezeOperator = FilterQueryOp.LessThan;
                break;
            case FilterOperators.LessThanOrEqual:
                breezeOperator = FilterQueryOp.LessThanOrEqual;
                break;
            case FilterOperators.NotEquals:
                breezeOperator = FilterQueryOp.NotEquals;
                break;
            case FilterOperators.Contains:
                breezeOperator = FilterQueryOp.Contains;
                // Making contains operator case insensitive.
                return Predicate.create('tolower(' + this.property + ')', breezeOperator, (encoder.encodeValue(this.value + '')).toLowerCase());
            default:
                throw new Error('Operator not supported by property filter for ' + this.property);
        }
        switch (entityType.getDataProperty(this.property).dataType) {
            case DataType.DateTime:
                // Vanwege IE, gebruik moment voor string naar date.
                return Predicate.create(this.property, breezeOperator, moment(this.value).toDate());
            default:
                return Predicate.create(this.property, breezeOperator, encoder.encodeValue(this.value));
        }
    };
    return PropertyFilter;
}());
export { PropertyFilter };
